import { createQueryKeys } from "@lukemorales/query-key-factory";
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { usePaginateQuery } from "@/hooks";
import { api } from "@/lib/api";
import { type Paginate, type SearchRequest } from "@/types";

import { type ApplicationVersion } from "../types";

export type ApplicationVersionList = Paginate<ApplicationVersion<false, true>>;

export const applications = createQueryKeys("application-versions", {
  all: (page?: number, filter?: SearchRequest) => [{ page, filter }],
  one: (id?: number) => [id],
});

const include = "flows.children";

export const fetchApplicationVersionsQueryOptions = (
  page: number = 0,
  search?: SearchRequest,
) =>
  queryOptions({
    ...applications.all(page, search),
    queryFn: () =>
      api
        .get<ApplicationVersionList>(`application/application-versions`, {
          params: {
            include,
            page,
            ...search,
          },
        })
        .then((response) => response.data),
  });

export const useFetchApplicationVersions = (search?: SearchRequest) =>
  usePaginateQuery((page) =>
    fetchApplicationVersionsQueryOptions(page, search),
  );

export const fetchApplicationVersionQueryOptions = (id: number | undefined) =>
  queryOptions({
    ...applications.one(id),
    queryFn: () =>
      api
        .get<
          ApplicationVersion<true, true>
        >(`application/application-versions/${id}?include=${include}`)
        .then((response) => response.data),
    enabled: id !== undefined,
  });

export const useFetchApplicationVersion = (id: number | undefined) =>
  useQuery(fetchApplicationVersionQueryOptions(id));

export const useFetchBookmarkApplicationVersions = () =>
  useFetchApplicationVersions({ filters: { is_bookmarked: 1 } });

export const useBookmarkApplicationVersion = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api
        .put(
          `application/application-versions/${id}/bookmark?include=${include}`,
        )
        .then((response) => response.data),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({
        queryKey: applications.all._def,
      });
      queryClient.invalidateQueries(applications.one(id));
    },
  });
};

export const useUnbookmarkApplicationVersion = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api
        .delete(
          `application/application-versions/${id}/bookmark?include=${include}`,
        )
        .then((response) => response.data),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({
        queryKey: applications.all._def,
      });
      queryClient.invalidateQueries(applications.one(id));
    },
  });
};
